import { createAction } from '@reduxjs/toolkit';
import { adminActions } from 'store/admin';
import { appActions } from 'store/app';
import { clientTxnOppsActions } from 'store/clientTxnOpps';
import { dashboardActions } from 'store/dashboard';
import { exploreDataActions } from 'store/explore/data';
import { exploreLoadingActions } from 'store/explore/loading';
import { exploreMapActions } from 'store/explore/map';
import { exploreProspectsActions } from 'store/explore/prospects';
import { exploreShapesDataActions } from 'store/explore/shapesData';
import { exploreViewsActions } from 'store/explore/views';
import { importActions } from 'store/import/import';
import { searchActions } from 'store/search';
import { auctionUWActions } from 'store/underwrite/auctionUW';
import { channelWorkflowActions } from 'store/underwrite/channelWorkflow';
import { propertyUWActions } from 'store/underwrite/propertyUW';
import { queueActions } from 'store/queue';
import { opportunityHistoryActions } from 'store/underwrite/opportunityHistory';
import { assumptionModulesActions } from 'store/assumptions/assumptionModules';
import { assumptionGroupsActions } from './assumptions/assumptionGroups';
import { assumptionGridActions } from './assumptions/assumptionGrid';
import { assumptionMapActions } from './assumptions/assumptionMap';
import { postAcqUWActions } from 'store/postAcq/postAcqUW';
import { postAcqWorkflowActions } from 'store/postAcq/postAcqWorkflow';

// APP
export const {
  setUser,
  setUsers,
  setConnectionState,
  setServicesStatus,
  setVersionRefreshModalIsOpen,
  setAppSettings,
  setConfirmationModalState,
  appNotify,
  closeSnackbar,
  removeSnackbar,
  setUserLocation,
  addSignalRQueuedUpdate,
  removeSignalRQueuedUpdates,
  checkVersion,
  fetchAppSettings,
  pingServices,
  fetchUsers,
  fetchUser,
  updateClientTxnOppsFromQueued,
  setGridStateById,
  setShowErrorListModal,
  setErrorListModalState,
  setIsMobile,
  setDisplayMode
} = appActions;

// ADMIN
export const {
  updateAppMessage,
  updateAppMessages,
  fetchAppMessages,
  deleteAppMessage,
  createAppMessage,
  setProcessingItems,
  processingItemsService,
  clientSwitchMaintenanceMode,
  adminSwitchMaintenanceMode,
  checkVersionForMaintenance,
  setCurrentAppVersion,
  setSelectedAdminTab,
  fetchProcessingHomes,
  setProcessingHomes,
  setProcessingHomesIsLoading
} = adminActions;

export const setSelectedTask = createAction('setSelectedTask');

// CLIENT TXN OPPS
export const {
  setClientTxnOpps,
  setIsFetchingClientTxnOppsData,
  setWorkflowGridNeedsToRefresh,
  updateClientTxnOpps,
  deleteTxnOpps,
  reloadTxnOpps,
  editTxnOpp,
  onNewTasksFromImport,
  assignTasksTo,
  unassignTasks,
  terminateTxnOpps,
  unterminateTxnOpps,
  updateVisibleTxnOpps,
  orderInspection,
  refreshTxnInspection,
  updateBidInPrism,
  syncPurchaseState,
  runStatusOverrideSync,
  submitPreScreenToUnderwrite,
  exportAllocationLogs,
  getDescriptiveFitDetails,
} = clientTxnOppsActions;

// CHANNEL WORKFLOW
export const {
  setChannelWorkflowGridState,
  setChannelWorkflowGridStateUpdate,
  setChannelWorkflowGridDataUpdate,
  setDescriptiveFitDetails,
  setChannelWorkflowGridLayouts,
  setChannelWorkflowGridSelectedLayoutId,
  setChannelWorkflowGridLayoutIsDiverted,
  setChannelWorkflowLoadingOverlayState,
  fetchChannelWorkflowGridLayouts,
  saveChannelWorkflowGridLayout,
  updateChannelWorkflowGridLayout,
  deleteChannelWorkflowGridLayout,
  favoriteChannelWorkflowGridLayout,
  setChannelWorkflowGridFetchError,
  setChannelWorkflowGridStableState,
} = channelWorkflowActions;

// PROPERTY UW
export const {
  setPropertyUWClientTxnOpp,
  setImagesCache,
  setImagesRequests,
  setSelectedOpportunityCensusTracts,
  setSelectedOpportunityMsrHomes,
  setSelectedOpportunityReloadStatusState,
  setSelectedOpportunityAmherstIndicative,
  toggleTxnOppProformaIsOpen,
  setSelectedCT,
  setCTMouseoverData,
  setShowCTMouseoverData,
  setCTLegendData,
  setSelectedCompDataType,
  setSelectedProformaInputsContainer,
  setSelectedValuation,
  setTransientValuation,
  setLastCompletedValuation,
  setValuationIsDirty,
  setShouldCalculateProforma,
  setSaveUWDialogParameters,
  setSelectedOpportunityComps,
  setSelectedOpportunityImages,
  setProformaWidth,
  setIsSavingUW,
  setPropertyUWMapState,
  setPropertyUWMarkerState,
  setPropertyUWMsrHomesMapState,
  setPropertyUWMsrHomesMarkerState,
  setPropertyGridCurrentViewData,
  setPropertyGridRowIdToFocus,
  setPropertyUWMsrHomesGridCurrentViewData,
  setIsFetchingContainers,
  setIsFetchingMsrHomes,
  setIsFetchingTransactionEvents,
  setIsFetchingPropertyHistoryOpportunities,
  setIsFetchingValuationHistory,
  setIsFetchingPropertyUWData,
  setIsFetchingLastCompletedValuation,
  setIsFetchingPropertyUWImages,
  setIsSyncingPurchaseState,
  setPropertyUWLoadingOverlayState,
  setCompsLoadingState,
  setIsRefreshingModelTax,
  setSelectedContainerId,
  setSelectedContainer,
  setViewContainersDialogIsOpen,
  setContainers,

  fetchUWData,
  fetchUWSnapshotData,
  fetchLastCompletedValuation,
  fetchPropertyUWCensusTract,
  fetchPropertyUWContainers,
  fetchPropertyUWImages,
  fetchPropertyUWReloadStatus,
  fetchMsrHomes,
  fetchTransactionEvents,
  fetchOpportunitiesForPropertyHistory,
  fetchValuationHistory,
  saveUW,
  savePostAcqUW,
  afterSaveUWNavigate,
  takeValuation,
  setTransientValuationInputs,
  setTransientValuationNoBidReason,
  setTransientValuationType,
  setTransientValuationNotes,
  calcPropertyUWProforma,
  toggleIsSelectedAsComp,
  refreshModelTax,
  setTransientValuationAfterTaxRefresh,
  setValuationTypeModalOptions,
  fetchOpportunitySfhaDetails,
  setOpportunitySfhaDetails,
  setIsOpportunitySfhaDetailLoading,
  setSelectedOpportunityHistory,
  setSelectedOpportunityEventHistory,  
  setSelectedOpportunityValuationHistory,
  setSelectedOpportunityHistoryTab
} = propertyUWActions;

// AUCTION UW
export const {
  setSelectedAuctionTxnOpps,
  setSelectedAuctionICBMStrategies,
  setSelectedAuctionTxnOppValuations,
  setIsCompleteAuctionModalOpen,
  setAuctionUWMapState,
  setAuctionUWMarkerState,
  setAuctionGridCurrentViewData,
  setAuctionGridState,
  toggleViewValuationDialogIsOpen,
  setViewValuationDialogProps,
  setIsFetchingAuctionUWData,
  runAutoCalc,
  completeAuction,
  fetchTxnOppValuations,
} = auctionUWActions;

// QUEUE
export const {
  setQueueTxnOpps,
  setIsFetchingQueueTxnOppsData,
  setQueueTxnOppsNeedToRefresh,
  setQueueFilterInput,
  setShowPostAcquisitions,
  setQueueSortInput,
  setOriginalMyTasksOrder,
  setQueueDrawerIsOpen,
  setFilteredTasks,
  setQueueBinsState,
  clearCompletedQueue,
  fetchQueueTxnOppsData,
  changeQueueSort,
} = queueActions;

// IMPORT
export const {
  setImportOpportunityStatus,
  setImportName,
  setImportNameValidation,
  setImportSelectedTab,
  setOpportunityImportSelectedChannel,
  setPostAcqTaskImportChannel,
  setPostAcqTaskImportRequestType,
  setImportRows,
  setImportGridEditDirty,
  setImportGeoValidationResult,
  setImportDupCheckErrors,
  setImportRowsSkippedCount,
  setImportHistoryData,
  setImportStatuses,
  setImportLoadingOverlayState,
  revalidateImportRows,
  validateGeoOnServer,
  autoFillDescriptives,
  submitImport,
  setImportRawData,
  fetchRejectedImportsOpportunities,
  addStatusToImportStatuses,
  setIsFromRejectedImports,
  setImportHistoryRefresh,
} = importActions;

// SEARCH
export const { setSearchState, fetchSearch, fetchSearchLastPricedByMe } =
  searchActions;

// EXPLORE
export const { createExplorePropertyDataObjectInstance, deleteExplorePropertyDataObjectInstance, setExplorePropertyDataObjects,
  updateExplorePropertyDataObjectById, setExploreSelectedIds, selectExploreMapPin,
  setExplorePropertyDetailsData, setExplorePropertyUnderwritesData, setSelectedPropertyUnderwrite, setExploreSearch, setExploreFilter,
  setExploreGeoFilter, setSavedFilterId, setExplorePropertyData, setResultsGridDataUpdate, setIsMainDataLoading, setIsSecondaryDataLoading,
  getExploreSelectedProperties, setExplorePushProperties,
  setExploreChartsBucketsData, setExplorePinsAreVisible, setPinColorIds, setPinColorPaletteId,
  setExploreRequestedGroupLevel, setExploreRequestedPinDisplayType, setExplorePinDisplayType, fetchExplorePropertyData,
  cancelExploreDataFetch, fetchExplorePropertyUnderwrites, setIsFetchingPropertyUWHistory } = exploreDataActions;

export const { createExploreShapesDataObjectInstance, deleteExploreShapesDataObjectInstance, setExploreShapesDataObjects, updateExploreShapesDataObjectById, setExploreSelectedShapeId,
  setExploreShapesData, setExploreShapesColoringType, setExploreShapesRequestedGeoType, setExploreShapesIsVisible,
  setExploreShapesIsLoading, fetchExploreShapesData } = exploreShapesDataActions;

export const { setExplorePins, setExploreLocation, toggleExploreFilterEditor, toggleExploreCustomShapesEditor, setExploreCustomShape,
  setExploreShapes, setExplorePropertyDetailsBoxIsOpen, setExplorePropertyDetailsIsOpen, setExploreViewUnderwriteModalIsOpen, setExploreSearchPin,
  setExploreMapType, updateExploreMarkerOptions, setExploreGridIsCollapsed, getPropertyDetailsDataForProspect, setIsRedirectToExplore
} = exploreMapActions;

export const { setExploreIsLoading, setExploreChartsBucketsIsLoading } =
  exploreLoadingActions;

export const { setProspectsPropertyDetailsData } = exploreProspectsActions;

export const {
  setExploreViews,
  setExploreViewsLoadingState,
  setExploreSelectedViewId,
  setExploreSelectedViewIsOpen,
  setExploreSelectedViewLoadingState,
  setExploreSelectedViewIsDirty,
  setExploreSelectedView,
  setExploreFocusedObjectId,
  setExploreHighlightedObjectId,
  setExploreSelectedObjectId,
  setExploreLastCreatedObjectId,
  setExploreLastCreatedLayerId,
  setExploreSaveViewDialogParameters,
  setExploreActiveDrawingObject,
  setExploreActiveEditingObject,
  updateSelectedExploreView,
  createExploreViewObject,
  updateExploreObjectById,
  createExploreViewLayer,
  deleteExploreViewLayer,
  deleteExploreViewObject,
  fetchExploreViews,
  createExploreView,
  createExploreSystemView,
  saveExploreView,
  deleteExploreView,
  updateExploreViewSharing,
  setExploreViewSharedWithUsers,
  requestExploreSelectedViewIdChange,
  setRadius,
  setManualRadius,
  setDistanceMeasuringTotalArea,
  setDistanceMeasuringTotalDistance,
  updateExploreViewsOrder,
  sortExploreViews,
  filterExploreViews,
  highlightExploreViewObject,
  handleExploreViewObjectVisibilityChange,
  setIsSideBarOpen
} = exploreViewsActions;

// DASHBOARD
export const {
  setSelectedTab,
  toggleDashboardFilterEditor,
  setDashboardFilter,
  setDashboardRules,
  setDashboardChannels,
  setDashboardTasks,
  setDashboardAvailableTasks,
  fetchDashboardTasks,
  fetchDashboardAvailableTasks,
  setDashboardActiveWorkflowTasks,
  fetchDashboardActiveWorkflowTasks,
  setDashboardTerminatedTasks,
  setDashboardClosedTasks,
  fetchDashboardTaskCountByStatus,
  setMyTasksSelectedUsers,
  setMyTasksIsLoading,
  setAvailableTasksIsLoading,
  setActiveWorkflowIsLoading,
  setCompletedTasksIsLoading,
  setSavedFilters,
  setInventoryData,
  fetchInventoryData,
  setRegions,
  fetchRegions,
  setStrategies,
  fetchStrategies,
  setIsLoading,
  setFromTime,
  setToTime,
  setSelectedStrategies,
  setStrategiesOpen,
  setAllStrategiesIncluded,
  setTbbpThreshold,
  setDashboardMaxPriceByRegion,
  setIdSearchResults,
  setIsSearchingIds,
  clearIdSearchResults,
  fetchIdSearchResults,
} = dashboardActions;

// ASSUMPTION MODULES
export const {
  // modules
  setModules,
  setModulesAreLoading,
  setSelectedModule,
  setSelectedModuleId,
  setSelectedModuleIsLoading,
  setSelectedModuleIsOpen,
  requestSelectedModuleIdChange,
  setSaveModuleDialogParameters,

  // sagas modules
  fetchAssumptionModules,
  createAssumptionModule,
  updateAssumptionModule,
  deleteAssumptionModule,
} = assumptionModulesActions;

// ASSUMPTION GROUPS
export const {
  setGroups,
  setGroupsAreLoading,
  setSelectedGroup,
  setSelectedGroupId,
  copyAssumptionGroup,
  setSelectedGroupIsOpen,
  setSelectedGroupIsLoading,
  setSelectedGroupIsDirty,
  updateGroupShape,
  setPublishGroupModalOpen,

  // shapes
  setCustomShapes,
  setNewShapeName,
  setSelectedCustomShape,
  setSelectedShapes,
  setCreatingNewShape,
  setShowCustomShapes,
  setShowPasteGeosModal,
  setCopyShapesModalOpen,

  // fields
  setFieldsAreDirty,

  // field values
  setShowFieldValueGrid,
  updateFieldValues,

  // sagas
  fetchAssumptionGroups,
  createAssumptionGroup,
  updateAssumptionGroup,
  publishAssumptionGroup,
  deleteAssumptionGroup,

  // sagas shapes
  fetchCustomShapes,
  createShape,
  deleteShape,
  updateShape,
  fetchCustomShapesForCopy,
  copyShapesForGroup,
} = assumptionGroupsActions;

// ASSUMPTION GRID
export const { resetChanges, setChanges, setEditRowKey, setGridIsLoading } =
  assumptionGridActions;

// ASSUMPTION MAP
export const {
  setSelectedShapeId,
  setMapIsLoading,
  setShapesData,
  setDropDownGeoType,
  setShapesGeoType,
  setMapLocation,
  setShapes,
  setColorPaletteId,
  setShapesColoringType,
  setMapType,
  setSelectedShapeFilter,
} = assumptionMapActions;

// OPPORTUNITY HISTORY
export const {
  setOpportunityHistoryTxnOppId,
  setIsOpportunityHistoryLoading,
  setIsViewOpportunityHistoryModalOpen,
  setOpportunityHistory,
  setEventSummaryRowData,
  setIsOpportunityHistoryDetailsModalLoading,
  setIsOpportunityHistoryDetailsModalOpen,
  setOpportunityHistoryDetailsEditHistories,
  setOpportunityHistoryDetailsStatusHistories,
  setOpportunityHistoryDetailsTaxesHistories,
  setOpportunityHistoryDetailsCrmSubmissionHistories,
  setOpportunityHistoryDetailsAllocationHistories,
  setOpportunityHistoryDetailsCrmReceiptHistories,
  setOpportunityHistoryDetailsIcbmOrderHistories,
  setOpportunityHistoryDetailsValuationHistories,
  setOpportunityHistoryProformaInputsContainers,
  setOpportunity,
  setOpportunityHistoryDetailsProspects,
  setOpportunityHistoryDetailsRejectedImports,
  setOpportunityHistoryDetailsAssignmentHistory,
  setOpportunityHistoryDetailsUnderwriteStatusHistory,
  setOpportunityHistoryTransactionOpportunityHistories,
  setOpportunityHistoryDetailsCompHistories,
  setOpportunityHistoryDetailsImportSaveHistories,
  setOpportunityHistoryDetailsImportPropMasterHistories,
  setOpportunityHistoryDetailsImportHomesHistories,
  setOpportunityHistoryDetailsRegionsAndIndicativesHistories,
  setOpportunityHistoryDetailsMarketHistories,
  setOpportunityHistoryDetailsReapirEstimateHistories,
  setOpportunityHistoryDetailsExpectedSalesPriceHistories,
  setOpportunityHistoryDetailsInsurancesHistories,
  setOpportunityHistoryDetailsAllocationLogs,
  setOpportunityHistoryDetailsImportLogs,
  setOpportunityEventHistories,
  closeViewOpportunityDetails,
  closeViewOpportunityHistoryDetails,
  // sagas
  fetchOpportunityHistory,
  fetchOpportunityHistoryTableDetails,
  fetchOpportunityHistoryLogDetails, 
} = opportunityHistoryActions;

// POST ACQ UW
export const {
  afterSavePostAcqUWNavigate,
  fetchPostAcqTaskData,
  fetchPostAcqTaskHistory,
  fetchPostAcqUnderwriteModalData,
  fetchPostAcqUWCensusTract,
  fetchPostAcqUWContainers,
  fetchPostAcqUWImages,
  fetchPostAcqUWMsrHomes,
  fetchPostAcqCma,
  savePostAcqModuleUW,
  setIsFetchingPostAcqContainers,
  setIsFetchingPostAcqTaskHistory,
  setIsFetchingPostAcqUWData,
  setIsFetchingPostAcqUWImages,
  setIsFetchingPostAcqUWMsrHomes,
  setIsSavingPostAcqUW,
  setPostAcqContainers,
  setPostAcqCTLegendData,
  setPostAcqCTMouseoverData,
  setPostAcqHasInputLimitError,
  setPostAcqProformaWidth,
  setPostAcqPropertyGridCurrentViewData,
  setPostAcqPropertyGridRowIdToFocus,
  setPostAcqSaveUWDialogParameters,
  setPostAcqSelectedCompDataType,
  setPostAcqSelectedContainer,
  setPostAcqSelectedContainerId,
  setPostAcqSelectedCT,
  setPostAcqSelectedOpportunityAmherstIndicative,
  setPostAcqSelectedOpportunityCensusTracts,
  setPostAcqSelectedOpportunityComps,
  setPostAcqSelectedOpportunityImages,
  setPostAcqSelectedProformaInputsContainer,
  setPostAcqSelectedTaskEventHistory,
  setPostAcqSelectedTaskHistory,
  setPostAcqSelectedTaskHistoryTab,
  setPostAcqSelectedValuation,
  setPostAcqShowCTMouseoverData,
  setPostAcqTransientValuation,
  setPostAcqTransientValuationInputs,
  setPostAcqTransientValuationNotes,
  setPostAcqUWClientTask,
  setPostAcqUWCompsImageCache,
  setPostAcqUWCompsImageRequests,
  setPostAcqUWMapState,
  setPostAcqUWMarkerState,
  setPostAcqUWMarketData,
  setPostAcqUWMsrHomes,
  setPostAcqUWMsrHomesGridCurrentViewData,
  setPostAcqUWMsrHomesMapState,
  setPostAcqUWMsrHomesMarkerState,
  setPostAcqValuationIsDirty,
  setPostAcqViewContainersDialogIsOpen,
  setPostAcqViewUnderwriteModalIsOpen,
  takePostAcqValuation,
  toggleIsSelectedAsPostAcqComp,
  togglePostAcqProformaIsOpen,
  setPostAcqCmaResult,
  setIsFetchingPostAcqCma,
  setPostAcqCmaDialogIsOpen
} = postAcqUWActions;

// POST ACQ WORKFLOW
export const {
  setPostAcqWorkflowGridState,
  setPostAcqWorkflowGridStateUpdate,
  setPostAcqWorkflowGridDataUpdate,
  setPostAcqWorkflowLoadingOverlayState,
  loadPostAcqTasks,
  updatePostAcqWorkflowTasks,
  setIsFetchingPostAcqWorkflowData,
  setPostAcqWorkflowGridNeedsToRefresh,
  closePostAcqTasks,
  openPostAcqTasks,
  deletePostAcqTasks,
  setPostAcqWorkflowGridLayouts,
  setPostAcqWorkflowGridSelectedLayoutId,
  setPostAcqWorkflowGridLayoutIsDiverted,
  assignPostAcqTasksTo,
  unassignPostAcqTasks,
  editPostAcqTasks
} = postAcqWorkflowActions;